import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { UseFormGetValues, UseFormWatch } from 'react-hook-form';
import { useOrderStore } from '../../../store/order-store';
import { useUIStore } from '../../../store/ui-store';
import OrderUser from './OrderUser';
import { Seo } from '../../../components/seo';
import StatusForm from './StatusForm';
import OrderTag from '../../../components/OrderTag';
import { useAuthStore } from '../../../store/auth-store';
import { Button } from '../../../components/inputs/button';
import useAlertStore from '../../../store/alert';
import { useAppContext } from '../../../App';
import { UserUtils } from '../../../utils/User';
import OrderTypeTag from '../../../components/OrderTypeTag';

export interface RefFormHandler {
  onSubmit: (data: unknown) => Promise<void>;
  getValues: UseFormGetValues<Record<string, string>>;
  watch?: UseFormWatch<Record<string, string>>;
  reset?: () => void;
}

function OrderDetailPage() {
  const { alert } = useAlertStore();
  const formRef = useRef<RefFormHandler>(null);
  const { id } = useParams();
  const { setTitle, setBreadcrumbs } = useUIStore();
  const { globalNotif } = useAppContext();
  const {
    currentOrder,
    getDetailedOrder,
    clearCurrentOrder,
    deleteOrder,
    deleteOrderStatus,
  } = useOrderStore();
  const navigate = useNavigate();
  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken?.payload, [idToken]);

  useEffect(() => {
    getDetailedOrder(Number(id));
  }, [id, getDetailedOrder]);

  useEffect(() => () => clearCurrentOrder(), [clearCurrentOrder]);

  useEffect(() => {
    const { firstName } = currentOrder?.user || {};
    setTitle(`${firstName}'s Order`);

    setBreadcrumbs([{ label: 'All orders', link: generatePath('/orders') }]);
  }, [currentOrder, setBreadcrumbs, setTitle]);

  const dateView = useMemo(() => {
    if (currentOrder) {
      return DateTime.fromJSDate(new Date(currentOrder.orderDate))
        .setZone('America/New_York')
        .toFormat('ccc, LLL d, yyyy hh:mm a ZZZZ');
    }
    return '';
  }, [currentOrder]);

  const deleteOrderConfirm = useCallback(async () => {
    alert(
      'Delete Order',
      `Are you sure you want to permanently delete this order? If a test result has been uploaded for this order, it will also be deleted.

<i>Note: This cannot be undone. Once deleted, it cannot be recovered.</i>
    `,
      [
        { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
        {
          text: 'Yes',
          onClick: async () => {
            if (id) {
              await deleteOrder({ id: Number.parseInt(id, 10) });
              globalNotif?.success({ message: `Order successfully deleted.` });
              navigate('/orders');
            }
          },
        },
      ],
    );
  }, [alert, deleteOrder, id, navigate, globalNotif]);

  return (
    <>
      <Seo title={`Order No: ${currentOrder?.shopifyId || '-'}`} />
      <div className="p-8 max-w-[1300px]">
        <div className="shadow rounded-lg">
          <div className="border-b border-gray-300 py-[10px] px-[24px] flex justify-between items-center">
            <div>
              <div className="flex flex-row items-start">
                <h4 className="text-2xl font-semibold mr-2">
                  Order No: {currentOrder?.name}
                </h4>
                {currentOrder ? <OrderTypeTag order={currentOrder} /> : null}
                {currentOrder ? <OrderTag order={currentOrder} /> : null}
              </div>
              <span className="text-base text-gray-500">{dateView}</span>
            </div>
            <div>
              {UserUtils.isSuperUser(cUser) ? (
                <Button
                  label="Delete Order"
                  onClick={deleteOrderConfirm}
                  loading={deleteOrderStatus.isLoading}
                />
              ) : null}
            </div>
          </div>

          <div className="flex flex-col md:flex-row flex-wrap">
            <div className="w-1/3 md:flex-1 min-w-[350px] max-w-[550px] p-[24px]">
              {currentOrder && <OrderUser order={currentOrder} />}
            </div>
            <div className="flex flex-1 min-w-[350px] p-[24px] border-b md:border-b-0">
              {currentOrder && idToken?.jwtToken && idToken?.jwtToken !== '' ? (
                <StatusForm ref={formRef} order={currentOrder} />
              ) : (
                <div className="bg-gray-300 h-[50px] w-[75%]" />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderDetailPage;
